import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, Users, MessageSquare, TrendingUp, Clock, CheckCircle, 
  XCircle, Upload, Plus, Eye, Play, Pause, FileText, AlertCircle,
  Link2, Ticket, Shield
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { LegalGatePanel } from './LegalGatePanel';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: { es: 'Borrador', en: 'Draft' }, color: 'bg-gray-100 text-gray-700', icon: FileText },
  pending_review: { label: { es: 'En Revisión', en: 'Under Review' }, color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  approved: { label: { es: 'Aprobada', en: 'Approved' }, color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  active: { label: { es: 'Activa', en: 'Active' }, color: 'bg-green-100 text-green-700', icon: Play },
  paused: { label: { es: 'Pausada', en: 'Paused' }, color: 'bg-orange-100 text-orange-700', icon: Pause },
  completed: { label: { es: 'Completada', en: 'Completed' }, color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  rejected: { label: { es: 'Rechazada', en: 'Rejected' }, color: 'bg-red-100 text-red-700', icon: XCircle }
};

export const ReactivationDashboard = ({ businessId, kupons = [] }) => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith('es');
  
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [uploadedContacts, setUploadedContacts] = useState([]);
  const [availableLandings, setAvailableLandings] = useState([]);

  // Form state - now supports both kupon and landing
  const [formData, setFormData] = useState({
    name: '',
    kupon_id: '',
    description: '',
    business_website: '',
    target_offer: '',
    days_inactive: 2,
    accepted_dpa: false,
    // Landing page options
    use_landing: false,
    landing_id: ''
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/reactivation/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAvailableLandings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/reactivation/campaigns/available-landings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableLandings(response.data.landings || []);
    } catch (error) {
      console.error('Error fetching landings:', error);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchAvailableLandings();
  }, [fetchCampaigns, fetchAvailableLandings]);

  // File upload handling
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Map columns to expected format
        const contacts = jsonData.map(row => ({
          name: row.nombre || row.name || row.Nombre || row.Name || '',
          phone: String(row.telefono || row.phone || row.Telefono || row.Phone || row.movil || row.Movil || ''),
          email: row.email || row.Email || row.correo || row.Correo || '',
          last_contact_date: row.ultima_visita || row.last_visit || row.fecha || ''
        })).filter(c => c.phone);

        setUploadedContacts(contacts);
        toast.success(isSpanish 
          ? `${contacts.length} contactos cargados` 
          : `${contacts.length} contacts loaded`);
      } catch (error) {
        console.error('Error parsing file:', error);
        toast.error(isSpanish ? 'Error al leer el archivo' : 'Error reading file');
      }
    };
    reader.readAsArrayBuffer(file);
  }, [isSpanish]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    maxFiles: 1
  });

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    
    // Validate: must select either kupon or landing
    if (!formData.use_landing && !formData.kupon_id) {
      toast.error(isSpanish ? 'Selecciona un Pase VIP o una Landing Page' : 'Select a VIP Pass or a Landing Page');
      return;
    }
    
    if (formData.use_landing && !formData.landing_id) {
      toast.error(isSpanish ? 'Selecciona una Landing Page' : 'Select a Landing Page');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Build payload
      const payload = {
        name: formData.name,
        business_id: businessId,
        description: formData.description,
        business_website: formData.business_website,
        target_offer: formData.target_offer,
        days_inactive: formData.days_inactive,
        use_landing: formData.use_landing,
        kupon_id: formData.use_landing ? null : formData.kupon_id,
        landing_id: formData.use_landing ? formData.landing_id : null
      };
      
      // Create campaign
      const response = await axios.post(`${API}/api/reactivation/campaigns`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const campaignId = response.data.campaign_id;

      // Upload contacts if any
      if (uploadedContacts.length > 0) {
        await axios.post(`${API}/api/reactivation/campaigns/${campaignId}/contacts`, {
          campaign_id: campaignId,
          contacts: uploadedContacts
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success(isSpanish ? 'Campaña creada' : 'Campaign created');
      setShowCreateForm(false);
      setFormData({
        name: '',
        kupon_id: '',
        description: '',
        business_website: '',
        target_offer: '',
        days_inactive: 2,
        accepted_dpa: false,
        use_landing: false,
        landing_id: ''
      });
      setUploadedContacts([]);
      fetchCampaigns();
      fetchAvailableLandings();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error al crear campaña' : 'Error creating campaign'));
    }
  };

  const handleSubmitForReview = async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/reactivation/campaigns/${campaignId}/submit`, null, {
        params: { accepted_dpa: true },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isSpanish ? 'Campaña enviada a revisión' : 'Campaign submitted for review');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error' : 'Error'));
    }
  };

  const activeKupons = kupons.filter(k => k.is_active);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-500" />
            {isSpanish ? 'Campañas de Reactivación' : 'Reactivation Campaigns'}
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            {isSpanish 
              ? 'Reactiva clientes inactivos con IA conversacional'
              : 'Reactivate inactive customers with conversational AI'}
          </p>
        </div>
        <Button
          onClick={() => setShowCreateForm(true)}
          className="bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="create-campaign-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          {isSpanish ? 'Nueva Campaña' : 'New Campaign'}
        </Button>
      </div>

      {/* Alert if no active Kupons AND no landings */}
      {activeKupons.length === 0 && availableLandings.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">
              {isSpanish ? 'Necesitas un Pase VIP o una Landing Page' : 'You need a VIP Pass or a Landing Page'}
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              {isSpanish 
                ? 'Crea un Pase VIP o una Landing Page primero para poder lanzar campañas de reactivación.'
                : 'Create a VIP Pass or a Landing Page first to launch reactivation campaigns.'}
            </p>
          </div>
        </div>
      )}

      {/* Create Campaign Form */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">
                {isSpanish ? 'Crear Nueva Campaña' : 'Create New Campaign'}
              </h3>
              
              <form onSubmit={handleCreateCampaign} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {isSpanish ? 'Nombre de la Campaña' : 'Campaign Name'} *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={isSpanish ? 'Ej: Reactivación Abril 2026' : 'E.g.: April 2026 Reactivation'}
                      required
                      data-testid="campaign-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {isSpanish ? 'Oferta para Reactivación' : 'Reactivation Offer'} *
                    </label>
                    <Input
                      value={formData.target_offer}
                      onChange={(e) => setFormData(prev => ({ ...prev, target_offer: e.target.value }))}
                      placeholder={isSpanish 
                        ? 'Ej: 20% de descuento en tu próxima visita'
                        : 'E.g.: 20% off your next visit'}
                      required
                      data-testid="campaign-offer-input"
                    />
                  </div>
                </div>

                {/* Campaign Type Selector: Kupon vs Landing */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <label className="text-sm font-medium text-gray-700 mb-3 block">
                    {isSpanish ? '¿Cómo quieres dirigir a los clientes?' : 'How do you want to direct customers?'} *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Option: Use Kupon */}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, use_landing: false, landing_id: '' }))}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        !formData.use_landing 
                          ? 'border-amber-500 bg-amber-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="use-kupon-option"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          !formData.use_landing ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-500'
                        }`}>
                          <Ticket className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {isSpanish ? 'Usar Pase VIP' : 'Use VIP Pass'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {isSpanish ? 'Cupón con descuento directo' : 'Coupon with direct discount'}
                          </p>
                        </div>
                      </div>
                    </button>

                    {/* Option: Use Landing */}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, use_landing: true, kupon_id: '' }))}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        formData.use_landing 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="use-landing-option"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          formData.use_landing ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'
                        }`}>
                          <Link2 className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {isSpanish ? 'Usar Landing Page' : 'Use Landing Page'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {isSpanish ? 'Página con reserva de citas' : 'Page with appointment booking'}
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Kupon Selector (if use_landing = false) */}
                {!formData.use_landing && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {isSpanish ? 'Pase VIP a Ofrecer' : 'VIP Pass to Offer'} *
                    </label>
                    <select
                      value={formData.kupon_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, kupon_id: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      data-testid="kupon-selector"
                    >
                      <option value="">{isSpanish ? 'Selecciona un Pase VIP...' : 'Select a VIP Pass...'}</option>
                      {activeKupons.map(k => (
                        <option key={k._id} value={k._id}>
                          {k.title} - {k.discount_type === 'percentage' ? `${k.discount_value}%` : `€${k.discount_value}`}
                        </option>
                      ))}
                    </select>
                    {activeKupons.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        {isSpanish ? 'No tienes Pases VIP activos. Crea uno primero o usa una Landing Page.' : 'No active VIP Passes. Create one first or use a Landing Page.'}
                      </p>
                    )}
                  </div>
                )}

                {/* Landing Selector (if use_landing = true) */}
                {formData.use_landing && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      {isSpanish ? 'Landing Page a Vincular' : 'Landing Page to Link'} *
                    </label>
                    <select
                      value={formData.landing_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, landing_id: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="landing-selector"
                    >
                      <option value="">{isSpanish ? 'Selecciona una Landing...' : 'Select a Landing...'}</option>
                      {availableLandings.map(l => (
                        <option key={l.landing_id} value={l.landing_id}>
                          {l.title} ({l.url})
                        </option>
                      ))}
                    </select>
                    {availableLandings.length === 0 && (
                      <p className="text-xs text-emerald-600 mt-1">
                        {isSpanish ? 'No tienes Landing Pages disponibles. Crea una en la sección "Mis Landings".' : 'No Landing Pages available. Create one in the "My Landings" section.'}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {isSpanish ? 'Descripción (opcional)' : 'Description (optional)'}
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={isSpanish 
                      ? 'Detalles adicionales sobre la campaña...'
                      : 'Additional campaign details...'}
                    rows={2}
                  />
                </div>

                {/* File Upload */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    {isSpanish ? 'Cargar Lista de Contactos' : 'Upload Contact List'}
                  </label>
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
                      ${isDragActive ? 'border-amber-500 bg-amber-50' : 'border-gray-300 hover:border-amber-400'}`}
                  >
                    <input {...getInputProps()} />
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    {uploadedContacts.length > 0 ? (
                      <p className="text-green-600 font-medium">
                        {uploadedContacts.length} {isSpanish ? 'contactos cargados' : 'contacts loaded'}
                      </p>
                    ) : (
                      <p className="text-gray-500 text-sm">
                        {isSpanish 
                          ? 'Arrastra un archivo Excel/CSV o haz clic para seleccionar'
                          : 'Drag an Excel/CSV file or click to select'}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {isSpanish 
                        ? 'Columnas: nombre, telefono, email (opcional), ultima_visita (opcional)'
                        : 'Columns: name, phone, email (optional), last_visit (optional)'}
                    </p>
                  </div>
                </div>

                {/* DPA Checkbox */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="campaign-dpa"
                      checked={formData.accepted_dpa}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, accepted_dpa: checked }))}
                    />
                    <label htmlFor="campaign-dpa" className="text-sm text-gray-600 cursor-pointer">
                      {isSpanish ? (
                        <>
                          Acepto el{' '}
                          <a href="/legal/dpa" target="_blank" className="text-amber-600 hover:underline">
                            Acuerdo de Procesamiento de Datos
                          </a>
                          {' '}y confirmo que tengo base legal para contactar a estos clientes.
                        </>
                      ) : (
                        <>
                          I accept the{' '}
                          <a href="/legal/dpa" target="_blank" className="text-amber-600 hover:underline">
                            Data Processing Agreement
                          </a>
                          {' '}and confirm I have legal basis to contact these customers.
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                  >
                    {isSpanish ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button
                    type="submit"
                    className="bg-amber-500 hover:bg-amber-600"
                    disabled={!formData.accepted_dpa || (activeKupons.length === 0 && availableLandings.length === 0)}
                    data-testid="create-campaign-submit-btn"
                  >
                    {isSpanish ? 'Crear Campaña' : 'Create Campaign'}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center">
          <Zap className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {isSpanish ? 'Sin campañas todavía' : 'No campaigns yet'}
          </h3>
          <p className="text-gray-500 mb-4">
            {isSpanish 
              ? 'Crea tu primera campaña de reactivación para empezar a recuperar clientes'
              : 'Create your first reactivation campaign to start recovering customers'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => {
            const statusConfig = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
            const StatusIcon = statusConfig.icon;
            
            return (
              <motion.div
                key={campaign._id}
                layout
                className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                      <p className="text-sm text-gray-500">
                        {isSpanish ? 'Creada:' : 'Created:'} {new Date(campaign.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${statusConfig.color}`}>
                      <StatusIcon className="w-4 h-4" />
                      {isSpanish ? statusConfig.label.es : statusConfig.label.en}
                    </span>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <Users className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                      <p className="text-xl font-bold text-gray-900">{campaign.metrics?.total_contacts || 0}</p>
                      <p className="text-xs text-gray-500">{isSpanish ? 'Contactos' : 'Contacts'}</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-3 text-center">
                      <MessageSquare className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                      <p className="text-xl font-bold text-blue-600">{campaign.metrics?.responded || 0}</p>
                      <p className="text-xs text-gray-500">{isSpanish ? 'Respuestas' : 'Responses'}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-3 text-center">
                      <CheckCircle className="w-5 h-5 mx-auto text-green-500 mb-1" />
                      <p className="text-xl font-bold text-green-600">{campaign.metrics?.qualified || 0}</p>
                      <p className="text-xs text-gray-500">{isSpanish ? 'Cualificados' : 'Qualified'}</p>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-3 text-center">
                      <TrendingUp className="w-5 h-5 mx-auto text-purple-500 mb-1" />
                      <p className="text-xl font-bold text-purple-600">{campaign.metrics?.converted || 0}</p>
                      <p className="text-xs text-gray-500">{isSpanish ? 'Convertidos' : 'Converted'}</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-3 text-center">
                      <span className="text-lg">€</span>
                      <p className="text-xl font-bold text-amber-600">
                        {(campaign.metrics?.total_revenue || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">{isSpanish ? 'Ingresos' : 'Revenue'}</p>
                    </div>
                  </div>

                  {/* Legal Gate Panel - RGPD Compliance */}
                  {campaign.metrics?.total_contacts > 0 && (
                    <div className="mb-4">
                      <LegalGatePanel 
                        campaignId={campaign._id} 
                        onStatusChange={(status) => {
                          // Refresh campaigns when Legal Gate status changes
                          if (status?.legal_gate_status === 'completed') {
                            fetchCampaigns();
                          }
                        }}
                      />
                    </div>
                  )}

                  {/* Linked Landing Page Info */}
                  {campaign.use_landing && campaign.landing_url && (
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                            <Link2 className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-emerald-800">
                              {isSpanish ? 'Landing Page Vinculada' : 'Linked Landing Page'}
                            </p>
                            <p className="text-sm text-emerald-600">{campaign.landing_url}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="text-center">
                            <p className="font-bold text-emerald-700">{campaign.metrics?.landing_views || 0}</p>
                            <p className="text-xs text-emerald-600">{isSpanish ? 'Visitas' : 'Views'}</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-emerald-700">{campaign.metrics?.landing_conversions || 0}</p>
                            <p className="text-xs text-emerald-600">{isSpanish ? 'Conversiones' : 'Conversions'}</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-emerald-700">{campaign.metrics?.landing_appointments || 0}</p>
                            <p className="text-xs text-emerald-600">{isSpanish ? 'Citas' : 'Appointments'}</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(campaign.landing_url, '_blank')}
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {isSpanish ? 'Ver' : 'View'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    <div className="text-sm text-gray-500">
                      {campaign.setup_fee > 0 && (
                        <span className="mr-4">
                          Setup: €{campaign.setup_fee}
                        </span>
                      )}
                      <span>
                        {isSpanish ? 'Comisión:' : 'Commission:'} {campaign.commission_percentage}%
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedCampaign(campaign)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {isSpanish ? 'Ver Detalles' : 'View Details'}
                      </Button>
                      {campaign.status === 'draft' && campaign.metrics?.total_contacts > 0 && (
                        <Button
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600"
                          onClick={() => handleSubmitForReview(campaign._id)}
                        >
                          {isSpanish ? 'Enviar a Revisión' : 'Submit for Review'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReactivationDashboard;
